import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { Crown, Loader2, MapPin, Clock, Shirt } from "lucide-react";
import { getNyeStatus, getNyeTicket } from "@/lib/api";

const ink = "#17130c";
const parchment = "#f4ecdd";
const gold = "#b98f2f";
const goldHi = "#d9b45c";
const oxblood = "#6e1423";

export default function NyeSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [ticket, setTicket] = useState(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    document.title = "YOU ARE IN — Wolfkeeper Masquerade";
    if (!sessionId) { setFailed(true); return; }
    let attempts = 0;
    let cancelled = false;
    const poll = async () => {
      try {
        const st = await getNyeStatus(sessionId);
        if (st.payment_status === "paid") {
          const t = await getNyeTicket(sessionId);
          if (!cancelled) setTicket(t);
          return;
        }
        if (st.status === "expired" || st.status === "failed") {
          if (!cancelled) setFailed(true);
          return;
        }
      } catch { /* keep polling */ }
      attempts += 1;
      if (attempts < 20 && !cancelled) setTimeout(poll, 2000);
      else if (!cancelled) setFailed(true);
    };
    poll();
    return () => { cancelled = true; };
  }, [sessionId]);

  return (
    <div data-testid="nye-success-page" className="min-h-screen flex items-center justify-center px-5 py-16"
      style={{ background: parchment, color: ink }}>
      {!ticket && !failed && (
        <div data-testid="nye-confirming" className="text-center">
          <Loader2 size={32} className="animate-spin mx-auto" style={{ color: gold }} />
          <p className="mt-4 text-xs tracking-[0.3em] uppercase" style={{ fontFamily: "'IBM Plex Mono', monospace" }}>
            Sealing your invitation…
          </p>
        </div>
      )}

      {failed && (
        <div data-testid="nye-payment-incomplete" className="text-center max-w-md">
          <h1 className="text-3xl" style={{ fontFamily: "'Bodoni Moda', serif" }}>Payment not confirmed</h1>
          <p className="mt-4 text-sm" style={{ color: `${ink}b3` }}>
            We could not confirm your payment. If you completed checkout, your ticket email will arrive shortly —
            otherwise no charge was made and your tickets were released.
          </p>
          <Link to="/nye" data-testid="nye-back-link"
            className="inline-block mt-8 px-8 py-3 text-[11px] tracking-[0.3em] uppercase"
            style={{ background: oxblood, color: parchment }}>
            Return to the Ball
          </Link>
        </div>
      )}

      {ticket && ticket.status === "confirmed" && (
        <motion.div initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.6 }}
          data-testid="nye-ticket" className="w-full max-w-lg border"
          style={{ borderColor: gold, background: "#fbf6ea", boxShadow: `0 30px 60px -30px rgba(23,19,12,0.4)` }}>
          <div className="py-6 text-center" style={{ background: ink }}>
            <div className="text-[10px] tracking-[0.5em] uppercase" style={{ color: gold, fontFamily: "'IBM Plex Mono', monospace" }}>
              Wolfkeeper University
            </div>
            <h1 className="mt-2 text-3xl sm:text-4xl" style={{ fontFamily: "'Bodoni Moda', serif", color: parchment }}>
              YOU ARE IN
            </h1>
          </div>

          <div className="p-7 sm:p-9">
            <div className="text-center">
              <div className="text-[10px] tracking-[0.35em] uppercase" style={{ color: gold, fontFamily: "'IBM Plex Mono', monospace" }}>
                {ticket.tier_label}
              </div>
              <div data-testid="nye-ticket-tier" className="mt-1 text-2xl" style={{ fontFamily: "'Bodoni Moda', serif" }}>
                {ticket.tier_name} × {ticket.quantity}
              </div>
              {ticket.buyer_name && (
                <div data-testid="nye-ticket-name" className="mt-2 text-sm" style={{ color: `${ink}b3` }}>
                  {ticket.buyer_name}
                </div>
              )}
            </div>

            <div className="my-6 flex justify-center">
              <img data-testid="nye-ticket-qr" src={ticket.qr_url} alt="Entry QR code" width={180} height={180}
                style={{ border: `4px solid ${ink}` }} />
            </div>

            <div data-testid="nye-ticket-order-code" className="text-center text-sm tracking-[0.25em]"
              style={{ fontFamily: "'IBM Plex Mono', monospace", color: oxblood }}>
              {ticket.order_code}
            </div>

            <div className="mt-6 border-t pt-5 space-y-2 text-xs tracking-[0.15em] uppercase"
              style={{ borderColor: `${gold}55`, fontFamily: "'IBM Plex Mono', monospace", color: `${ink}cc` }}>
              <div className="flex items-center justify-center gap-2"><Clock size={12} style={{ color: oxblood }} /> {ticket.event_date}</div>
              <div className="flex items-center justify-center gap-2"><MapPin size={12} style={{ color: oxblood }} /> {ticket.location_line}</div>
              <div className="flex items-center justify-center gap-2"><Shirt size={12} style={{ color: oxblood }} /> Masquerade attire — masks required</div>
            </div>

            <p className="mt-6 text-center text-xs leading-relaxed" style={{ color: `${ink}99` }}>
              {ticket.buyer_email ? (
                <>A confirmation email with this ticket and QR code has been sent to <strong>{ticket.buyer_email}</strong>. </>
              ) : (
                <>Your ticket is confirmed — save or screenshot this page. </>
              )}
              Present the QR code at the door — one scan admits your party.
            </p>

            <div className="mt-6 flex items-center justify-center gap-2 text-[10px] tracking-[0.4em] uppercase"
              style={{ color: gold, fontFamily: "'IBM Plex Mono', monospace" }}>
              <Crown size={12} /> The pack awaits
            </div>
          </div>
        </motion.div>
      )}
    </div>
  );
}
